import { cliente } from '@/lib/cliente';
import type {
  GuardarMapeoRecetaRequest,
  ListaBomBCResponse,
  ListaProductosEnsambladosBCResponse,
  ListaRecetasBlendMapeoResponse,
  RecetaBlendMapeo,
  RespuestaApi,
} from '@adelante/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

/**
 * Mapeo Receta Blend → Producto BC. Catálogo chico, cambia poco → cache
 * moderado (`staleTime: 5 min`).
 */
export function useRecetasBlendMapeo() {
  return useQuery<RespuestaApi<ListaRecetasBlendMapeoResponse>>({
    queryKey: ['mapeo-recetas'],
    queryFn: () => cliente.mapeoRecetas.listar(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Catálogo de productos ensamblados de BC (categoría CONCRETO_PREMEZ), en
 * vivo desde Business Central. Cache largo: no cambia en el día a día.
 */
export function useProductosEnsambladosBC() {
  return useQuery<RespuestaApi<ListaProductosEnsambladosBCResponse>>({
    queryKey: ['bc-productos-ensamblados'],
    queryFn: () => cliente.bcCatalogo.productosEnsamblados(),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

/**
 * BOM ("Assembly BOM") de un producto de BC — materiales + recurso con su
 * cantidad por unidad del producto padre. `enabled: !!codigo` para no
 * pegarle a BC cuando la colada todavía no tiene producto mapeado.
 */
export function useBomProductoBC(codigo: string | null) {
  return useQuery<RespuestaApi<ListaBomBCResponse>>({
    queryKey: ['bc-bom', codigo],
    queryFn: () => cliente.bcCatalogo.bom(codigo as string),
    enabled: !!codigo,
    staleTime: 10 * 60 * 1000,
    retry: false,
  });
}

export function useGuardarMapeoReceta() {
  const qc = useQueryClient();
  return useMutation<RespuestaApi<RecetaBlendMapeo>, Error, GuardarMapeoRecetaRequest>({
    mutationFn: (body) => cliente.mapeoRecetas.guardar(body),
    onSuccess: (r) => {
      if (r.data) {
        toast.success('Mapeo guardado');
        qc.invalidateQueries({ queryKey: ['mapeo-recetas'] });
        // Las coladas afectadas por el backfill pueden haber cambiado su
        // estado de mapeo BC — refrescar listados ('coladas', plural) Y el
        // detalle abierto ('colada', singular — key distinta, hay que
        // invalidar las dos o la pantalla de detalle queda con el dato viejo
        // hasta que se recargue a mano).
        qc.invalidateQueries({ queryKey: ['coladas'] });
        qc.invalidateQueries({ queryKey: ['colada'] });
      } else {
        toast.error(r.error?.mensaje ?? 'Error desconocido');
      }
    },
    onError: (e) => {
      toast.error('No se pudo guardar el mapeo', { description: e.message });
    },
  });
}
